/**
 * @fileoverview Centralized configuration for image upload system
 * Single source of truth for all image upload constraints and defaults
 */

/**
 * Accepted image file formats
 */
export const ACCEPTED_IMAGE_FORMATS = {
  JPEG: "image/jpeg",
  PNG: "image/png",
  WEBP: "image/webp"
} as const

/**
 * Array of accepted MIME types for file input
 */
export const ACCEPTED_FORMATS_ARRAY: readonly string[] = Object.values(ACCEPTED_IMAGE_FORMATS)

/**
 * Comma-separated string for HTML accept attribute
 */
export const ACCEPTED_FORMATS_STRING = ACCEPTED_FORMATS_ARRAY.join(",")

/**
 * Human-readable format names
 */
export const FORMAT_NAMES = "JPEG, PNG, WebP"

/**
 * File size constraints
 */
export const FILE_SIZE_LIMITS = {
  MAX_SIZE_BYTES: 5 * 1024 * 1024, // 5MB in bytes
  MAX_SIZE_MB: 5,
  MAX_SIZE_STRING: "5MB"
} as const

/**
 * Image dimension constraints
 */
export const IMAGE_DIMENSIONS = {
  MIN_WIDTH: 100,
  MIN_HEIGHT: 100,
  DEFAULT_MAX_WIDTH: 512,
  DEFAULT_MAX_HEIGHT: 512,
  CROP_MIN_WIDTH: 100,
  CROP_MIN_HEIGHT: 100
} as const

/**
 * Processing defaults
 */
export const PROCESSING_DEFAULTS = {
  QUALITY: 0.8,
  FORMAT: "webp" as const,
  ASPECT_RATIO: 1, // Square (1:1)
  USE_WEB_WORKER: true
} as const

/**
 * Default upload configuration
 */
export const DEFAULT_UPLOAD_CONFIG = {
  aspectRatio: PROCESSING_DEFAULTS.ASPECT_RATIO,
  maxSize: FILE_SIZE_LIMITS.MAX_SIZE_STRING,
  quality: PROCESSING_DEFAULTS.QUALITY,
  format: PROCESSING_DEFAULTS.FORMAT,
  dimensions: {
    width: IMAGE_DIMENSIONS.DEFAULT_MAX_WIDTH,
    height: IMAGE_DIMENSIONS.DEFAULT_MAX_HEIGHT
  },
  title: "Upload Image",
  description: "Select and crop your image"
} as const

/**
 * Profile picture specific config
 */
export const PROFILE_PICTURE_CONFIG = {
  aspectRatio: 1, // Always square
  maxSize: FILE_SIZE_LIMITS.MAX_SIZE_STRING,
  quality: PROCESSING_DEFAULTS.QUALITY,
  format: "webp" as const,
  dimensions: {
    width: 512,
    height: 512
  },
  title: "Upload Profile Picture",
  description: "Select and crop your profile picture"
} as const

/**
 * Generated image sizes (for responsive images)
 */
export const RESPONSIVE_IMAGE_SIZES = {
  TINY: 20,
  SMALL: 40,
  MEDIUM: 80,
  LARGE: 160,
  XLARGE: 320,
  XXLARGE: 640
} as const

/**
 * Size keys for responsive images (matches backend)
 */
export const RESPONSIVE_SIZE_KEYS = ["@20", "@40", "@80", "@160", "@320", "@640"] as const

/**
 * Validation error messages
 */
export const VALIDATION_ERRORS = {
  FILE_TOO_LARGE: `File size must be less than ${FILE_SIZE_LIMITS.MAX_SIZE_STRING}`,
  INVALID_FORMAT: `Only ${FORMAT_NAMES} images are allowed`,
  DIMENSIONS_TOO_SMALL: `Image must be at least ${IMAGE_DIMENSIONS.MIN_WIDTH}x${IMAGE_DIMENSIONS.MIN_HEIGHT} pixels`,
  INVALID_FILE: "Invalid image file",
  PROCESSING_FAILED: "Failed to process image",
  UPLOAD_FAILED: "Upload failed. Please try again.",
  CROP_FAILED: "Failed to crop image"
} as const

/**
 * Helper to validate file size
 */
export function isFileSizeValid(fileSize: number): boolean {
  return fileSize <= FILE_SIZE_LIMITS.MAX_SIZE_BYTES
}

/**
 * Helper to validate file format
 */
export function isFileFormatValid(mimeType: string): boolean {
  return ACCEPTED_FORMATS_ARRAY.includes(mimeType)
}

/**
 * Helper to validate image dimensions
 */
export function areDimensionsValid(width: number, height: number): boolean {
  return width >= IMAGE_DIMENSIONS.MIN_WIDTH && height >= IMAGE_DIMENSIONS.MIN_HEIGHT
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
}
